/**
 * DroneDetectionSetup.tsx — v0.4.9.4.6 (placeholder) — target v0.6+
 *
 * Drone Detection (DR). Will use a combination of:
 *   - Wi-Fi CSI sensing (the v0.6 capability — captures prop-wash signatures)
 *   - Visual recognition (drone silhouette + RF cross-cue)
 *
 * Combining both modalities is more accurate than either alone:
 *   - CSI catches the propeller's micro-Doppler even when the drone is
 *     out of camera view (behind a wall, above a roofline, at night)
 *   - Visual catches the type / model / payload of the drone when it's
 *     in view, giving the operator actionable intel
 *
 * This is a placeholder until v0.6+ ships. The placeholder shows:
 *   - the planned dual-modality approach
 *   - the deployment scenarios (perimeter, event, border)
 *   - the open questions that need user input before coding starts
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Construction, Plane, Wifi, Camera, AlertTriangle } from 'lucide-react';

export default function DroneDetectionSetup() {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-amber-300 bg-amber-50/30 p-6 space-y-3">
        <div className="flex items-center gap-2">
          <Construction className="w-5 h-5 text-amber-700" />
          <h2 className="text-lg font-semibold text-amber-900">
            {t('aiTraining.comingSoonTitle')}
          </h2>
          <span className="px-2 py-0.5 rounded text-xs bg-amber-200 text-amber-900">
            {t('aiTraining.targetVersion', { version: '0.6+' })}
          </span>
        </div>
        <p className="text-sm text-gray-900/90">
          {t('aiTraining.drone.description')}
        </p>
      </div>

      {/* Dual-modality diagram */}
      <div className="rounded-lg border border-gray-300 bg-white p-5">
        <h3 className="text-sm font-semibold mb-3">{t('aiTraining.drone.dualModalityTitle')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Modality
            Icon={Wifi}
            title={t('aiTraining.drone.csi.title')}
            body={t('aiTraining.drone.csi.body')}
          />
          <Modality
            Icon={Camera}
            title={t('aiTraining.drone.visual.title')}
            body={t('aiTraining.drone.visual.body')}
          />
        </div>
      </div>

      {/* Deployment scenarios */}
      <div className="rounded-lg border border-gray-300 bg-white p-5">
        <h3 className="text-sm font-semibold mb-3">{t('aiTraining.drone.scenariosTitle')}</h3>
        <ul className="space-y-2 text-xs text-gray-900/80">
          <li>• {t('aiTraining.drone.scenarios.perimeter')}</li>
          <li>• {t('aiTraining.drone.scenarios.event')}</li>
          <li>• {t('aiTraining.drone.scenarios.border')}</li>
        </ul>
      </div>

      {/* Open questions */}
      <div className="rounded-lg border border-gray-300 bg-white p-5">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          {t('aiTraining.drone.openQuestionsTitle')}
        </h3>
        <ul className="space-y-2 text-xs text-gray-900/80">
          <li>• {t('aiTraining.drone.questions.q1')}</li>
          <li>• {t('aiTraining.drone.questions.q2')}</li>
          <li>• {t('aiTraining.drone.questions.q3')}</li>
        </ul>
      </div>

      <p className="text-xs text-gray-600">
        {t('aiTraining.comingSoonFootnote')}
      </p>
    </div>
  );
}

function Modality({ Icon, title, body }: { Icon: React.ComponentType<any>; title: string; body: string }) {
  return (
    <div className="rounded border border-gray-300 bg-gray-50/30 p-4 space-y-2">
      <div className="flex items-center gap-2 text-gray-900">
        <Icon className="w-4 h-4" />
        <div className="font-medium text-sm">{title}</div>
      </div>
      <p className="text-xs text-gray-600">{body}</p>
    </div>
  );
}
